'use client'

import { useEffect, useState, useRef } from 'react'
import { getAccount } from '@/lib/auth'

export default function OptInsPage() {
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [cursor, setCursor] = useState(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('all') // all | ordered | never
  const [sendingId, setSendingId] = useState(null)
  const [composerOpen, setComposerOpen] = useState(false)
  const [composeText, setComposeText] = useState('')
  const [activeUser, setActiveUser] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [composeSending, setComposeSending] = useState(false)
  const composeRef = useRef(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const acc = await getAccount()
      if (!mounted) return
      if (!acc) {
        window.location.href = '/login'
        return
      }
      // Subscription guard
      try {
        const res = await fetch('/api/billing/me', { method: 'GET', credentials: 'include', cache: 'no-store' })
        if (res.ok) {
          const data = await res.json()
          const status = (data?.stripe?.status || '').toLowerCase()
          const subscribed = ['active','trialing','past_due'].includes(status)
          if (!subscribed) {
            window.location.href = '/subscription'
            return
          }
        } else {
          window.location.href = '/subscription'
          return
        }
      } catch {
        window.location.href = '/subscription'
        return
      }
      setAccount(acc)
      await loadUsers()
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  // Auto-apply search as the user types (debounced)
  useEffect(() => {
    if (!account) return
    const handle = setTimeout(() => {
      loadUsers(null)
    }, 350)
    return () => clearTimeout(handle)
  }, [q, status, account])

  async function loadUsers(next = null) {
    setFetching(true)
    try {
      const url = new URL('/api/users/opted-in', window.location.origin)
      if (next) url.searchParams.set('cursor', next)
      if (q.trim()) url.searchParams.set('q', q.trim())
      if (status && status !== 'all') url.searchParams.set('status', status)
      const res = await fetch(url.toString(), { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setUsers(prev => (next ? [...prev, ...data.items] : data.items))
        setCursor(data.nextCursor || null)
      }
    } finally {
      setFetching(false)
    }
  }

  // Formatters
  const fmtPhone = (p) => {
    if (!p) return ''
    const d = String(p).replace(/\D/g, '')
    const digits = d.startsWith('1') && d.length === 11 ? d.slice(1) : d
    if (digits.length !== 10) return p
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
  }
  const fmtCurrency = (n) => {
    const v = Number(n || 0)
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
  }
  const fmtLastOrder = (iso) => {
    if (!iso) return 'Never'
    try {
      const d = new Date(iso)
      if (isNaN(d)) return 'Never'
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch { return 'Never' }
  }
  const initials = (name, email) => {
    const n = (name || '').trim() || (email || '').trim()
    if (!n) return '?'
    const parts = n.split(/\s+/)
    const first = parts[0]?.[0] || ''
    const second = parts[1]?.[0] || ''
    return (first + second).toUpperCase()
  }

  const insertVar = (token) => {
    const ta = composeRef.current
    if (!ta) {
      setComposeText(prev => (prev + (prev && !prev.endsWith(' ') ? ' ' : '') + token))
      return
    }
    try {
      const start = ta.selectionStart ?? composeText.length
      const end = ta.selectionEnd ?? composeText.length
      const before = composeText.slice(0, start)
      const after = composeText.slice(end)
      const next = before + token + after
      setComposeText(next)
      setTimeout(() => {
        try { ta.focus(); const pos = start + token.length; ta.setSelectionRange(pos, pos) } catch {}
      }, 0)
    } catch {
      setComposeText(prev => (prev + token))
    }
  }

  async function sendSms(u, msg) {
    if (!msg || !msg.trim()) {
      setFeedback({ type: 'error', text: 'Enter a message first' })
      setTimeout(() => setFeedback(null), 1500)
      return
    }
    setSendingId(u.id)
    setComposeSending(true)
    try {
      const res = await fetch('/api/sms/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: u.userId, phone: u.phone, message: msg })
      })
      const body = await res.json().catch(() => ({}))
      if (res.ok && body?.ok) {
        setFeedback({ type: 'success', text: 'Message sent!' })
        setComposerOpen(false)
        setComposeText('')
      } else {
        setFeedback({ type: 'error', text: body?.error || 'Failed to send' })
      }
    } catch (e) {
      setFeedback({ type: 'error', text: e?.message || 'Failed to send' })
    } finally {
      setComposeSending(false)
      setSendingId(null)
      setTimeout(() => setFeedback(null), 2000)
    }
  }

  if (loading) return null

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Opted-in Users</h1>
        <p className="text-slate-400">Send a one-off SMS to any opted-in user. Messages include a STOP footer automatically.</p>
      </div>

      <div className="mb-4 flex gap-3 items-end justify-end">
        <div className="w-44 max-w-full">
          <label className="block text-sm text-slate-300 mb-1">Filter</label>
          <select
            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">All users</option>
            <option value="ordered">Ordered</option>
            <option value="never">Never ordered</option>
          </select>
        </div>
        {/* Sorting removed: default is Total Spent high to low on server */}
        <div className="w-80 max-w-full">
          <label className="block text-sm text-slate-300 mb-1">Search</label>
          <input
            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2"
            placeholder="name, email or phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadUsers(null) }}
          />
        </div>
      </div>

      {/* Column headers for alignment */}
      <div className="mb-2 hidden md:grid grid-cols-[minmax(0,1fr)_140px_80px_150px_auto] items-center gap-6 px-2 text-xs text-slate-500">
        <div></div>
        <div className="text-right">Total Spent</div>
        <div className="text-center">Orders</div>
        <div className="text-right">Last Order</div>
        <div></div>
      </div>

      {feedback && (
        <div className={`mb-4 rounded-lg p-3 ${feedback.type === 'success' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-red-900/40 text-red-300'}`}>
          {feedback.text}
        </div>
      )}

      <div className="space-y-3">
        {users.map(u => (
          <div key={u.id} className="grid grid-cols-[minmax(0,1fr)_140px_80px_150px_auto] items-center gap-6 rounded-xl border border-slate-800 bg-slate-900/70 p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-indigo-600/80 grid place-items-center text-white font-bold">
                {initials(u.customerName || u.firstName, u.email)}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-100 text-lg">{u.customerName || u.firstName || 'Unknown'}</span>
                  <span className="text-xs text-slate-400">ID: {u.userId}</span>
                </div>
                <div className="text-sm text-slate-300 flex flex-wrap gap-3">
                  <span>📞 {fmtPhone(u.phone)}</span>
                  {u.email && <span>✉️ {u.email}</span>}
                </div>
                {(u.address1 || u.city || u.state || u.postalCode) && (
                  <div className="text-xs text-slate-400 mt-1">
                    {[
                      u.address1,
                      u.address2,
                      [u.city, u.state].filter(Boolean).join(', '),
                      u.postalCode
                    ].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="text-xl font-semibold tabular-nums">{fmtCurrency(u.lifetimeSpend)}</div>
              <div className="text-xs text-slate-400">Total Spent</div>
            </div>

            <div className="text-center">
              <div className="text-xl font-semibold tabular-nums">{typeof u.orderCount === 'number' ? u.orderCount : '-'}</div>
              <div className="text-xs text-slate-400">Orders</div>
            </div>

            <div className="text-right">
              <div className="text-sm font-medium tabular-nums">{fmtLastOrder(u.lastOrderAt)}</div>
              <div className="text-xs text-slate-400">Last Order</div>
            </div>

            <button
              onClick={() => {
                setActiveUser(u)
                setComposeText('')
                setComposerOpen(true)
              }}
              disabled={sendingId === u.id || fetching}
              className="w-28 justify-center px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-50"
              style={{ backgroundColor: '#8B5CF6' }}
            >
              {sendingId === u.id ? 'Sending…' : 'Send SMS'}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-center">
        {cursor && (
          <button
            onClick={() => loadUsers(cursor)}
            className="px-4 py-2 rounded-lg text-white font-semibold"
            style={{ backgroundColor: '#374151' }}
          >
            {fetching ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>

      {/* SMS Composer Modal */}
      {composerOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => { if (!composeSending) setComposerOpen(false) }} />
          <div className="relative z-50 w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 p-5 shadow-2xl" aria-busy={composeSending}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="font-semibold text-lg">Send SMS</div>
                {activeUser && (
                  <div className="text-sm text-slate-400">
                    To: <span className="text-slate-200">{activeUser.customerName || activeUser.firstName || 'User'}</span> • {activeUser.phone}
                  </div>
                )}
              </div>
              <button
                className="px-2 py-1 text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { if (!composeSending) setComposerOpen(false) }}
                aria-label="Close"
                disabled={composeSending}
              >✕</button>
            </div>
            <textarea
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 disabled:opacity-50"
              rows={5}
              autoFocus
              ref={composeRef}
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              placeholder="Type your SMS..."
              disabled={composeSending}
            />
            <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
              <div>We append “STOP to opt out”.</div>
              <div>{composeText.length} chars</div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-slate-400">Variables:</span>
              <button
                type="button"
                onClick={() => insertVar('{{customerName}}')}
                className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-slate-200 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={composeSending}
              >
                {'{{customerName}}'}
              </button>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { if (!composeSending) setComposerOpen(false) }}
                disabled={composeSending}
              >Cancel</button>
              <button
                className="px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#8B5CF6' }}
                disabled={composeSending || !composeText.trim() || !activeUser}
                onClick={() => activeUser && sendSms(activeUser, composeText)}
              >
                {composeSending ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Sending…
                  </span>
                ) : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
